
import { useAuth } from "@/hooks/useAuth";
import { PatientInvitationForm } from "./PatientInvitationForm";
import { PatientInvitationList } from "./PatientInvitationList";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

export function PatientInvitationDashboard() {
  const { profile, isLoading } = useAuth();
  const [isDebugMode] = useState(process.env.NODE_ENV === 'development');
  
  // Debug profile data in development mode
  useEffect(() => {
    if (isDebugMode) {
      console.log('PatientInvitationDashboard - Profile data:', {
        profileLoaded: !!profile,
        clinicId: profile?.clinic_id,
        role: profile?.role,
        hasAccess: profile?.clinic_id && ['doctor', 'admin', 'staff'].includes(profile?.role || '')
      });
    }
  }, [profile, isDebugMode]);

  // Show loading state while profile data is being fetched
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-lg text-gray-700">Loading patient invitation dashboard...</span>
      </div>
    );
  }

  // Check if profile exists
  if (!profile) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
        <p className="text-gray-600">
          Please log in to access the patient invitation dashboard.
        </p>
      </div>
    );
  }

  // Only show this dashboard to doctors, admin, and staff with a clinic association
  if (!profile.clinic_id) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Clinic Association Required</h2>
        <p className="text-gray-600">
          You need to be associated with a clinic to access patient invitations.
          {isDebugMode && (
            <span className="block mt-2 text-xs text-red-500">
              Debug: profile.clinic_id is missing
            </span>
          )}
        </p>
      </div>
    );
  }

  // Check for appropriate role
  if (!['doctor', 'admin', 'staff'].includes(profile.role)) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
        <p className="text-gray-600">
          Only doctors, administrators, and staff can access patient invitations.
          {isDebugMode && (
            <span className="block mt-2 text-xs text-red-500">
              Debug: Current role: {profile.role || 'undefined'}
            </span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Patient Invitations</h1>
        <p className="text-gray-600 mt-2">
          Invite patients to join your clinic and manage existing invitations
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <PatientInvitationForm 
          clinicId={profile.clinic_id}
          onSuccess={() => {
            // The list will automatically refresh due to query invalidation
          }}
        />
        <div className="lg:col-span-2">
          <PatientInvitationList />
        </div>
      </div>
    </div>
  );
}
